import React from 'react';
import { 
  X, 
  Briefcase, 
  Target, 
  DollarSign, 
  Globe, 
  Building2,
  TrendingUp,
  MapPin
} from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Cell
} from 'recharts';
import './styles.scss';

const OccupationDetailModal = ({ isOpen, onClose, occupation }) => {
  if (!isOpen || !occupation) return null;

  // Prepare data for the percentile chart
  // Handling polymorphic field names across MSA/BOS/National datasets
  const median = occupation.annualMedian || 0;
  const p10 = occupation.annualPct10 || 0;
  const p25 = occupation.annualPct25 || 0;
  const p75 = occupation.annualPct75 || 0;
  const p90 = occupation.annualPct90 || 0;

  const chartData = [
    { name: '10th %ile', value: p10, color: '#94a3b8' },
    { name: '25th %ile', value: p25, color: '#60a5fa' },
    { name: 'Median', value: median, color: '#ed7534' },
    { name: '75th %ile', value: p75, color: '#10b981' },
    { name: '90th %ile', value: p90, color: '#f59e0b' },
  ].filter(d => d.value > 0);

  return (
    <div className="modal-overlay-glass" onClick={onClose}>
      <div 
        className="occupation-detail-modal-premium animate-scale-in" 
        onClick={(e) => e.stopPropagation()}
      >
        <header className="modal-header">
          <div className="header-content">
            <div className="icon-badge icon-badge--primary">
              <Briefcase size={24} />
            </div>
            <div className="title-stack">
              <span className="occ-code-pill">{occupation.occCode}</span>
              <h2>{occupation.occTitle}</h2>
            </div>
          </div>
          <button className="close-btn-round" onClick={onClose}>
            <X size={20} />
          </button>
        </header>

        <div className="modal-body-scrollable">
          <section className="metadata-summary-grid">
            <div className="meta-card">
              <MapPin size={18} className="meta-icon" />
              <div className="meta-info">
                <label>Regional Context</label>
                <p>{occupation.areaTitle}</p>
              </div>
            </div>
            <div className="meta-card">
              <Building2 size={18} className="meta-icon" />
              <div className="meta-info">
                <label>Industry (NAICS)</label>
                <p>{occupation.naicsTitle || 'Multisector / Cross-industry'}</p>
              </div>
            </div>
            <div className="meta-card">
              <TrendingUp size={18} className="meta-icon" />
              <div className="meta-info">
                <label>Employment Volume</label>
                <p>{Number(occupation.totalEmployment || 0).toLocaleString()} <small>Records</small></p>
              </div>
            </div>
            <div className="meta-card">
              <DollarSign size={18} className="meta-icon" />
              <div className="meta-info">
                <label>Median Annual</label>
                <p className="text-emerald">${Number(median).toLocaleString()}</p>
              </div>
            </div>
          </section>

          <section className="wage-distribution-deep-dive">
            <div className="section-header">
              <h3>
                <TrendingUp size={18} />
                Annual Wage Percentile Distribution
              </h3>
              <p>Income benchmarks across the 10th-90th percentile spectrum for {occupation.occTitle}.</p>
            </div>

            <div className="distribution-chart-container">
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData} margin={{ top: 20, right: 30, left: 40, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.1)" />
                    <XAxis 
                      dataKey="name" 
                      axisLine={false} 
                      tickLine={false}
                      tick={{ fill: '#94a3b8', fontSize: 12 }}
                    />
                    <YAxis 
                      axisLine={false} 
                      tickLine={false}
                      tick={{ fill: '#94a3b8', fontSize: 12 }}
                      tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                    />
                    <Tooltip 
                      cursor={{ fill: 'rgba(255,255,255,0.05)' }}
                      contentStyle={{ 
                        backgroundColor: 'rgba(15, 23, 42, 0.95)', 
                        border: 'none', 
                        borderRadius: '12px',
                        boxShadow: '0 10px 15px -3px rgba(0,0,0,0.3)',
                        backdropFilter: 'blur(8px)'
                      }}
                      itemStyle={{ color: '#fff', fontSize: '14px', fontWeight: '600' }}
                      labelStyle={{ color: '#94a3b8', marginBottom: '4px', fontSize: '12px' }}
                      formatter={(value) => [`$${value.toLocaleString()}`, 'Annual Wage']}
                    />
                    <Bar dataKey="value" radius={[6, 6, 0, 0]} barSize={40}>
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="distribution-empty-state">
                  <p>Detailed percentile distribution is not available for this regional record. Aggregated national benchmarks may be available in the BOS or National tabs.</p>
                </div>
              )}
            </div>
          </section>
        </div>

        <footer className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Dismiss
          </button>
        </footer>
      </div>
    </div>
  );
};

export default OccupationDetailModal;
